import { createContext, useContext, useEffect, useRef, useState } from "react";
import { WsConnectionManager } from "./wsConnectionManager";

const WsContext = createContext(null);

export function WsProvider({ children, walletAddress, enabled = true }) {
  const [manager, setManager] = useState(null);

  // Map<handlerId, { roomId, callback }>
  const roomHandlersRef = useRef(new Map());
  // Map<handlerId, callback>
  const globalHandlersRef = useRef(new Map());

  useEffect(() => {
    if (!enabled) {
      setManager(null);
      return;
    }

    const normalizedWallet = String(walletAddress || "").trim().toLowerCase();
    const newManager = new WsConnectionManager(normalizedWallet, (msg) => {
      if (!msg) return;

      if (msg.roomId) {
        roomHandlersRef.current.forEach(({ roomId, callback }) => {
          if (roomId === msg.roomId) callback(msg);
        });
      }

      globalHandlersRef.current.forEach((callback) => {
        callback(msg);
      });
    });

    newManager.connect();
    setManager(newManager);

    return () => {
      newManager.close();
      roomHandlersRef.current.clear();
      globalHandlersRef.current.clear();
      setManager(null);
    };
  }, [walletAddress, enabled]);

  const value = {
    manager,
    registerRoomHandler: (id, roomId, callback) => {
      roomHandlersRef.current.set(id, { roomId, callback });
    },
    unregisterRoomHandler: (id) => {
      roomHandlersRef.current.delete(id);
    },
    registerGlobalHandler: (id, callback) => {
      globalHandlersRef.current.set(id, callback);
    },
    unregisterGlobalHandler: (id) => {
      globalHandlersRef.current.delete(id);
    },
  };

  return <WsContext.Provider value={value}>{children}</WsContext.Provider>;
}

export function useWebsocket() {
  const ctx = useContext(WsContext);
  if (!ctx) {
    throw new Error("useWebsocket must be used within <WsProvider>");
  }
  return ctx;
}

export function useWebsocketMessages(onMessage) {
  const { registerGlobalHandler, unregisterGlobalHandler } = useWebsocket();
  const idRef = useRef(Symbol("ws-global-listener"));

  useEffect(() => {
    registerGlobalHandler(idRef.current, onMessage);
    return () => {
      unregisterGlobalHandler(idRef.current);
    };
  }, [registerGlobalHandler, unregisterGlobalHandler, onMessage]);
}
